import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSnackbar from 'shared/hooks/useSnackbar';
import { ACCOUNT_ROLES } from '@elementary/common-ui/constants/queryKeys';
import * as rolesApi from './rolesApi';
import { CreateRoleRequest, UpdateRoleRequest } from './types';

export const useRoles = () => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const {
    data: roles,
    isLoading,
    error,
  } = useQuery({
    queryKey: [ACCOUNT_ROLES],
    queryFn: rolesApi.fetchRoles,
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => rolesApi.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_ROLES] });
      openSnackbar({
        message: 'Role created successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to create role',
        variant: 'error',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({
      roleId,
      data,
    }: {
      roleId: string;
      data: UpdateRoleRequest;
    }) => rolesApi.updateRole(roleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_ROLES] });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_ROLES, variables.roleId],
      });
      openSnackbar({
        message: 'Role updated successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to update role',
        variant: 'error',
      });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: string) => rolesApi.deleteRole(roleId),
    onSuccess: (_, roleId) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_ROLES] });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_ROLES, roleId],
      });
      openSnackbar({
        message: 'Role deleted successfully',
        variant: 'success',
      });
    },
    onError: () => {
      openSnackbar({
        message: 'Failed to delete role',
        variant: 'error',
      });
    },
  });

  return {
    roles,
    isLoading,
    error,
    createRole: createRoleMutation.mutate,
    createRoleAsync: createRoleMutation.mutateAsync,
    isCreating: createRoleMutation.isLoading,
    updateRole: updateRoleMutation.mutate,
    updateRoleAsync: updateRoleMutation.mutateAsync,
    isUpdating: updateRoleMutation.isLoading,
    deleteRole: deleteRoleMutation.mutate,
    deleteRoleAsync: deleteRoleMutation.mutateAsync,
    isDeleting: deleteRoleMutation.isLoading,
  };
};
